import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Calendar, XCircle, CheckCircle2, Clock, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface Appointment {
  id: string;
  scheduled_date: string;
  duration_minutes: number;
  service_name: string | null;
  reason: string | null;
  notes: string | null;
  status: string;
  cancelled_at: string | null;
  created_at: string;
  google_event_id: string | null;
  cancellation_type: string | null;
}

interface LeadAppointmentHistoryProps {
  leadId: string;
  workspaceId: string;
}

export function LeadAppointmentHistory({ leadId, workspaceId }: LeadAppointmentHistoryProps) {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    const fetchAppointments = async () => {
      if (!workspaceId) return;
      setLoading(true);
      const { data } = await supabase
        .from("appointments")
        .select("*")
        .eq("lead_id", leadId)
        .eq("workspace_id", workspaceId)
        .order("scheduled_date", { ascending: false })
        .limit(20);

      setAppointments((data as Appointment[]) || []);
      setLoading(false);
    };

    fetchAppointments();
  }, [leadId, workspaceId]);

  if (loading) return null;
  if (appointments.length === 0) return null;

  const getStatusInfo = (appt: Appointment) => {
    const isPast = new Date(appt.scheduled_date) < new Date();
    if (appt.status === "cancelled" && appt.cancellation_type === "rescheduled") {
      return { icon: RefreshCw, label: "Reagendado", color: "text-orange-500", badgeClass: "bg-orange-500/10 text-orange-600" };
    }
    if (appt.status === "cancelled") {
      return { icon: XCircle, label: "Cancelado", color: "text-destructive", badgeClass: "bg-destructive/10 text-destructive" };
    }
    if (appt.status === "done" || (appt.status === "scheduled" && isPast)) {
      return { icon: CheckCircle2, label: "Concluído", color: "text-green-600", badgeClass: "bg-green-500/10 text-green-600" };
    }
    return { icon: Clock, label: "Agendado", color: "text-yellow-600", badgeClass: "bg-yellow-500/10 text-yellow-600" };
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="w-full justify-between px-0 hover:bg-transparent">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <h3 className="font-semibold text-sm">Histórico de Agendamentos</h3>
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
              {appointments.length}
            </Badge>
          </div>
          <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform", isOpen && "rotate-180")} />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2">
        <div className="space-y-3">
          {appointments.map((appt) => {
            const statusInfo = getStatusInfo(appt);
            const StatusIcon = statusInfo.icon;
            return (
              <div key={appt.id} className="flex items-start gap-3 text-sm">
                <StatusIcon className={cn("w-4 h-4 mt-0.5 shrink-0", statusInfo.color)} />
                <div className="flex-1 min-w-0 space-y-0.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">
                      {formatDate(appt.scheduled_date)} às {formatTime(appt.scheduled_date)}
                    </span>
                    <Badge variant="secondary" className={cn("text-[10px] px-1.5 py-0", statusInfo.badgeClass)}>
                      {statusInfo.label}
                    </Badge>
                  </div>
                  {(appt.service_name || appt.reason) && (
                    <p className="text-muted-foreground text-xs truncate">
                      {appt.service_name || appt.reason}
                    </p>
                  )}
                  {appt.status === "cancelled" && appt.cancelled_at && (
                    <p className="text-muted-foreground text-xs">
                      {appt.cancellation_type === "rescheduled" ? "Reagendado" : "Cancelado"} em {formatDate(appt.cancelled_at)}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
